import React, {
    ComponentType,
    FocusEvent,
    HTMLProps,
    ReactElement,
    useCallback,
    useState,
} from 'react';
import styled, { css } from 'styled-components';
import { shouldIgnoreProp } from '../../utils/shouldIgnoreProp';
import { Box, BoxProps } from '../layout/Box';

type TextControlProps = BoxProps & {
    disabled?: boolean;
    focused?: boolean;
    error?: boolean;
    variant?: 'filled' | 'standard';
};

const TextControl = styled(Box).withConfig({
    displayName: 'FormControl',
    shouldForwardProp: shouldIgnoreProp('disabled', 'focused', 'error', 'variant'),
})<TextControlProps>`
    display: flex;
    width: 100%;
    position: relative;
    align-items: baseline;
    transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    color: var(--color-primary);
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;

    ${(props) =>
        props.variant === 'filled' &&
        css`
            padding: 8px 16px;
            border-radius: 12px;
            color: var(--color-primary);
            background-color: var(--background-secondary);
        `}

    ${(props) =>
        props.variant === 'standard' &&
        css`
            border-bottom: 2px solid var(--divider);

            &::after {
                content: '';
                position: absolute;
                left: 0;
                right: 0;
                bottom: -2px;
                transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
                pointer-events: none;
                border-bottom: 2px solid var(--color-primary);
                transform: scaleX(0);
            }
        `}

    ${(props) =>
        props.error &&
        css`
            border-color: var(--color-danger);
        `};

    ${(props) =>
        props.focused &&
        !props.error &&
        !props.disabled &&
        css`
            &::after {
                transform: scale(1);
            }
        `};

    ${(props) =>
        props.disabled &&
        css`
            opacity: 0.5;
            cursor: not-allowed;
        `};
`;

export function makeTextControl<TElement extends HTMLElement>(
    component: ComponentType<HTMLProps<TElement>> | string,
) {
    interface ControlProps extends HTMLProps<TElement> {
        variant?: 'standard' | 'filled';
        error?: boolean;
        prefix?: string;
        rows?: number;
        startAdornment?: ReactElement;
        endAdornment?: ReactElement;
    }

    const Control = styled(component)<ControlProps>`
        resize: none;
        font: inherit;
        letter-spacing: inherit;
        color: var(--color-primary);
        padding: 4px 0 5px;
        border: 0;
        box-sizing: content-box;
        background: none;
        margin: 0;
        -webkit-tap-highlight-color: transparent;
        display: block;
        min-width: 0;
        width: 100%;
        outline: none;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        max-height: 50vh;

        ${(props) =>
            props.rows &&
            css`
                min-height: calc(20px * ${props.rows});
            `};

        &[disabled] {
            opacity: 0.5;
            cursor: not-allowed;
        }
    `;

    return (props: ControlProps) => {
        const { error, variant = 'standard', startAdornment, endAdornment, ...other } = props;
        const [focused, setFocused] = useState(false);

        const onFocus = useCallback(
            (event: FocusEvent<TElement>) => {
                props.onFocus?.(event);
                setFocused(true);
            },
            [props.onFocus],
        );

        const onBlur = useCallback(
            (event: FocusEvent<TElement>) => {
                props.onBlur?.(event);
                setFocused(false);
            },
            [props.onBlur],
        );

        return (
            <TextControl
                error={error}
                focused={focused}
                variant={variant}
                disabled={props.disabled}
            >
                {startAdornment}
                <Control {...other} onFocus={onFocus} onBlur={onBlur} />
                {endAdornment}
            </TextControl>
        );
    };
}
